// risk.ts — the in-browser Abandonment Risk Engine (R1).
//
// Computes a live 0-100 "intent-to-leave" score from a weighted, time-decayed
// blend of behavioral signals the tracker already observes:
//   exit_velocity    — cursor racing toward the top edge (classic exit precursor)
//   scroll_reversal  — a big upward scroll after going deep (sticker shock / flee)
//   idle             — went idle after being active
//   rage / dead      — frustration clicks (fed in from signals.ts)
//   error / network  — JS / request failures (fed in from errors.ts + tracker)
//   field_abandon    — focused a field, typed, then left without submitting
//   engagement_decay — interaction rate falling off over the session
//   rapid_back       — rapid history churn / back-button
//
// Design (evidence-backed, see docs/ANTI_ABANDONMENT_STRATEGY.md §2):
//   * Each contribution is a {points, ts} stamp. The instantaneous score is
//     the sum of contributions decayed by exp(-(now-ts)/TAU), capped at 100.
//     This gives transparent, hand-tunable "logistic-regression-style" scoring
//     with natural time-decay — exactly what mature CRM lead-scoring does, and
//     what ships without any training data.
//   * mouseleave / exit velocity is treated as the LAST-MOMENT trigger, not the
//     primary predictor — the composite is what matters (the research caveat).
//   * The engine NEVER touches layout and never blocks: its own listeners are
//     passive + throttled, and the tick is a 1s interval.
//
// It emits a `risk` event when the score crosses a band, spikes, or stays
// elevated, and publishes the live score on window.__hmRisk so popup.js (R2)
// can trigger on it with zero coupling.

import type { RiskEvent, RiskLevel } from "@hotmaps/shared/events";
import { classifyPageKind } from "@hotmaps/shared/api";

type RiskFactor =
  | "exit_velocity" | "scroll_reversal" | "idle" | "rage" | "dead"
  | "error" | "network" | "field_abandon" | "engagement_decay" | "rapid_back"
  | "coupon_hunt" | "payment_hesitation";

type Contribution = { factor: RiskFactor; points: number; ts: number };

export type RiskHandle = {
  /** Feed a discrete signal from elsewhere in the tracker. */
  notify: (factor: RiskFactor, points?: number) => void;
  /** Mark that the visitor did something engaged (resets idle, feeds rate). */
  activity: () => void;
  /** Reset per-page transient state (scroll depth, refractories) on SPA nav. */
  resetPage: () => void;
  /** Current instantaneous score 0-100. */
  getScore: () => number;
};

// Tuning constants. Points are additive into a decaying pool; thresholds match
// shared RISK_BANDS (low<35, medium 35-60, high 60-85, critical 85+).
const TAU_MS = 9000;            // decay time-constant — a spike fades over ~9s
const TICK_MS = 1000;
const IDLE_AFTER_MS = 11000;    // idle counts only after the visitor was active
const EMIT_MIN_SCORE = 30;      // don't bother emitting noise below this
const EMIT_MIN_SCORE_CHECKOUT = 20; // checkout is where money leaks — be twitchier
const EMIT_INTERVAL_MS = 4000;  // while elevated, refresh the timeline this often
const EMIT_DELTA = 18;          // or emit early on a jump this large
// R3 — on checkout pages every signal is amplified (the highest-value funnel).
const CHECKOUT_BOOST = 1.5;

// Default points per factor when notify() is called without an explicit value.
const DEFAULT_POINTS: Record<RiskFactor, number> = {
  exit_velocity: 34,
  scroll_reversal: 22,
  idle: 14,
  rage: 30,
  dead: 16,
  error: 10,
  network: 10,
  field_abandon: 26,
  engagement_decay: 12,
  rapid_back: 18,
  coupon_hunt: 24,
  payment_hesitation: 22,
};

// R3 — coupon / promo and payment field recognizers. Multilingual (he/en/ar).
const COUPON_RE = /(coupon|promo|discount|voucher|code|rebate|gift.?card|קופון|הנחה|מבצע|קוד.?הנחה|שובר|قسيمة|كوبون|خصم|رمز)/i;
const PAYMENT_RE = /(card.?number|cardnumber|cc-?number|cvv|cvc|csc|security.?code|expir|exp-?date|credit.?card|אשראי|כרטיס.?אשראי|מספר.?כרטיס|תוקף|cvv|بطاقة|رقم.?البطاقة|الأمان|انتهاء)/i;
function fieldText(el: Element): string {
  const i = el as HTMLInputElement;
  return [i.name, i.id, i.placeholder, i.getAttribute("aria-label"), i.getAttribute("autocomplete")]
    .filter(Boolean).join(" ");
}

function levelOf(score: number): RiskLevel {
  if (score >= 85) return "critical";
  if (score >= 60) return "high";
  if (score >= 35) return "medium";
  return "low";
}

export function registerRiskEngine(
  sessionStart: number,
  emit: (ev: RiskEvent) => void
): RiskHandle {
  const relTs = () => Date.now() - sessionStart;
  let contributions: Contribution[] = [];

  // Emission bookkeeping.
  let lastEmitAt = 0;
  let lastEmittedScore = 0;
  let lastLevel: RiskLevel = "low";

  // Activity / engagement tracking.
  let lastActivityAt = Date.now();
  let everActive = false;
  let idleCharged = false;
  let decayCharged = false;         // engagement_decay is charged ONCE per quiet episode
  // Engagement rate: count interactions per rolling window; a sharp drop after
  // a busy spell feeds engagement_decay. mousemove is capped to 1 unit/tick so
  // a moving cursor doesn't inflate the rate into a false "decay" when it pauses.
  let recentActivity = 0;
  let movedThisTick = false;
  let prevActivityRate = 0;

  // Per-factor refractory — the continuous gesture factors (exit_velocity,
  // scroll_reversal) fire on EVERY qualifying mousemove/scroll event, so a
  // single flick toward the navbar or one "back to top" would otherwise stack
  // dozens of bumps and peg the score. We accept at most one bump per factor
  // per window. Discrete factors (rage/dead/field_abandon/…) are not throttled.
  const REFRACTORY_MS: Partial<Record<RiskFactor, number>> = {
    exit_velocity: 1500,
    scroll_reversal: 1200,
  };
  const lastFactorBumpAt = new Map<RiskFactor, number>();

  // R3 — is the visitor on a checkout page right now? Re-evaluated per call so
  // it tracks SPA navigation between cart → checkout → confirm without state.
  function onCheckout(): boolean {
    return classifyPageKind(location.pathname) === "checkout";
  }

  function bump(factor: RiskFactor, points?: number) {
    let p = points ?? DEFAULT_POINTS[factor];
    if (p <= 0) return;
    const now = Date.now();
    const gap = REFRACTORY_MS[factor];
    if (gap !== undefined) {
      const last = lastFactorBumpAt.get(factor) ?? 0;
      if (now - last < gap) return;   // within the refractory window — ignore
      lastFactorBumpAt.set(factor, now);
    }
    if (onCheckout()) p *= CHECKOUT_BOOST; // amplify every signal in the funnel
    contributions.push({ factor, points: p, ts: now });
    // Keep the array bounded — old contributions decay to ~0 anyway.
    if (contributions.length > 64) contributions = contributions.slice(-48);
  }

  function decayedScore(now: number): { score: number; top: RiskFactor[] } {
    // Sum decayed contributions; also accumulate per-factor for the top list.
    const perFactor = new Map<RiskFactor, number>();
    let sum = 0;
    for (const c of contributions) {
      const age = now - c.ts;
      if (age > TAU_MS * 6) continue; // negligible
      const v = c.points * Math.exp(-age / TAU_MS);
      sum += v;
      perFactor.set(c.factor, (perFactor.get(c.factor) ?? 0) + v);
    }
    const score = Math.max(0, Math.min(100, Math.round(sum)));
    const top = Array.from(perFactor.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([f]) => f);
    return { score, top };
  }

  function maybeEmit(now: number) {
    const { score, top } = decayedScore(now);
    const level = levelOf(score);
    const bandChanged = level !== lastLevel;
    const elevated = score >= (onCheckout() ? EMIT_MIN_SCORE_CHECKOUT : EMIT_MIN_SCORE);
    const jumped = score - lastEmittedScore >= EMIT_DELTA;
    const stale = now - lastEmitAt >= EMIT_INTERVAL_MS;

    // Publish the live score on EVERY tick regardless of whether we persist an
    // event — popup.js (R2) reads window.__hmRisk and must see plateau values
    // in the 20-34 band too (previously frozen there). Cheap; no I/O.
    publish(score, level);

    // Always emit when the band changes (up OR down — the timeline wants the
    // recovery too). Otherwise emit while elevated on a jump or staleness.
    if (bandChanged || (elevated && (jumped || stale))) {
      // Suppress pure-low chatter: if we drop to low, emit once then go quiet.
      if (level === "low" && lastLevel === "low") return;
      lastEmitAt = now;
      lastEmittedScore = score;
      lastLevel = level;
      emit({
        t: "risk",
        ts: relTs(),
        score,
        level,
        factors: top,
        url: location.pathname || "/",
      });
    }
  }

  // --- live publication for popup.js (R2) ---------------------------------
  type RiskGlobal = { score: number; level: RiskLevel; subscribe: (cb: (s: number, l: RiskLevel) => void) => void };
  const subscribers: Array<(s: number, l: RiskLevel) => void> = [];
  function publish(score: number, level: RiskLevel) {
    const g = (window as unknown as { __hmRisk?: RiskGlobal });
    if (!g.__hmRisk) {
      g.__hmRisk = { score, level, subscribe: (cb) => subscribers.push(cb) };
    } else {
      g.__hmRisk.score = score;
      g.__hmRisk.level = level;
    }
    for (const cb of subscribers) { try { cb(score, level); } catch { /* swallow */ } }
  }
  publish(0, "low");

  // --- own passive listeners ----------------------------------------------
  // Exit velocity: cursor rising fast within the top band → exit precursor.
  let lastY = 0, lastT = 0;
  document.addEventListener("mousemove", (e) => {
    moveActivity();
    const t = performance.now();
    const dy = e.clientY - lastY;
    const dt = t - lastT;
    lastY = e.clientY; lastT = t;
    if (e.clientY > 80 || dt <= 0 || dy >= 0) return;
    const velocity = -dy / dt; // px/ms upward
    if (velocity < 0.5) return;
    // Scale points with velocity, capped. Faster + closer to the edge = scarier.
    const edgeBoost = e.clientY < 30 ? 1.3 : 1;
    bump("exit_velocity", Math.min(DEFAULT_POINTS.exit_velocity, velocity * 22 * edgeBoost));
  }, { capture: true, passive: true });

  // Scroll reversal / sticker shock: track depth + direction; a large upward
  // reversal after having gone deep is a flee signal.
  let lastScrollY = 0, maxDepthPx = 0;
  document.addEventListener("scroll", () => {
    activity();
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    if (y > maxDepthPx) maxDepthPx = y;
    const delta = y - lastScrollY;
    lastScrollY = y;
    // Upward jump (negative delta) of meaningful size after we'd scrolled deep.
    if (delta < -180 && maxDepthPx > window.innerHeight * 1.2) {
      bump("scroll_reversal", Math.min(DEFAULT_POINTS.scroll_reversal, (-delta) / 14));
    }
  }, { capture: true, passive: true });

  // Generic activity inputs.
  document.addEventListener("click", activity, { capture: true, passive: true });
  document.addEventListener("keydown", activity, { capture: true, passive: true });
  document.addEventListener("touchstart", activity, { capture: true, passive: true });

  // Rapid back / nav churn — patch history.back usage indirectly via popstate
  // frequency. Two popstates within 1.5s reads as comparison-shopping churn.
  let lastPop = 0;
  window.addEventListener("popstate", () => {
    const now = Date.now();
    if (now - lastPop < 1500) bump("rapid_back");
    lastPop = now;
  });

  function activity() {
    lastActivityAt = Date.now();
    everActive = true;
    idleCharged = false;
    decayCharged = false;   // a fresh interaction re-arms the decay detector
    recentActivity++;
  }
  // A moving cursor fires activity() ~10×/s; count that as at most ONE unit of
  // activity per tick so the engagement rate reflects distinct interactions,
  // not raw mousemove volume (else pausing to read looked like a huge "decay").
  function moveActivity() {
    lastActivityAt = Date.now();
    everActive = true;
    idleCharged = false;
    decayCharged = false;
    if (!movedThisTick) { recentActivity++; movedThisTick = true; }
  }

  // Reset per-page transient state on SPA navigation — called by the tracker's
  // history hook. Prevents a deep scroll on page A + scroll-to-top on page B
  // from firing a phantom scroll_reversal, and clears gesture refractories.
  function resetPage() {
    lastScrollY = 0;
    maxDepthPx = 0;
    lastFactorBumpAt.clear();
    prevActivityRate = 0;
  }

  // --- R3 checkout-specific detectors -------------------------------------
  // Coupon-hunt: opening a coupon/promo field is a documented abandonment
  // precursor (visitors leave to hunt a code, 27% don't return). We charge on
  // focus of a coupon field while on checkout; if they leave it without typing
  // we charge again (no code → likely to bail).
  let paymentFocusedAt = 0;
  let paymentTimer: number | undefined;
  document.addEventListener("focusin", (e) => {
    const el = e.target as Element | null;
    if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
    if (!onCheckout()) return;
    const blob = fieldText(el);
    if (COUPON_RE.test(blob)) {
      bump("coupon_hunt");
    } else if (PAYMENT_RE.test(blob)) {
      // Payment hesitation: a long dwell on a card field with no completion is
      // sticker-shock / second-thoughts. Arm a timer; fire if still focused.
      paymentFocusedAt = Date.now();
      if (paymentTimer) window.clearTimeout(paymentTimer);
      paymentTimer = window.setTimeout(() => {
        if (document.activeElement === el) bump("payment_hesitation");
      }, 7000);
    }
  }, { capture: true, passive: true });
  document.addEventListener("focusout", (e) => {
    const el = e.target as Element | null;
    if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
    if (paymentTimer) { window.clearTimeout(paymentTimer); paymentTimer = undefined; }
    if (!onCheckout()) return;
    const blob = fieldText(el);
    // Left a coupon field having typed nothing → almost certainly went hunting.
    if (COUPON_RE.test(blob) && !(el as HTMLInputElement).value) bump("coupon_hunt", 14);
    // Quick in-and-out of a payment field (focused < 2s) reads as hesitation.
    if (PAYMENT_RE.test(blob) && paymentFocusedAt && Date.now() - paymentFocusedAt < 2000) {
      bump("payment_hesitation", 12);
    }
  }, { capture: true, passive: true });

  // --- tick: decay, idle, engagement-rate, emit ---------------------------
  const tick = () => {
    const now = Date.now();
    // Idle: charge once when we first cross the idle threshold after activity.
    if (everActive && !idleCharged && now - lastActivityAt > IDLE_AFTER_MS) {
      bump("idle");
      idleCharged = true;
    }
    // Engagement decay: a busy session that suddenly flatlines (without going
    // fully idle) is disengaging — charged ONCE per quiet episode (decayCharged),
    // re-armed by the next real interaction. Without the flag this fired every
    // tick while the trailing rate bled down, pegging the score on readers.
    const rate = recentActivity;
    if (prevActivityRate >= 3 && rate === 0 && !idleCharged && !decayCharged) {
      bump("engagement_decay");
      decayCharged = true;
    }
    prevActivityRate = prevActivityRate * 0.6 + rate * 0.4;
    recentActivity = 0;
    movedThisTick = false;

    maybeEmit(now);
  };
  const tickTimer = window.setInterval(tick, TICK_MS);

  // Stop the loop when the page is being unloaded (one final emit so the peak
  // isn't lost). visibilitychange→hidden also raises risk slightly (the user
  // tabbed away — a real abandonment precursor).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      bump("exit_velocity", 12); // tab-away is a soft exit signal
      maybeEmit(Date.now());
    }
  });
  window.addEventListener("pagehide", () => {
    try { clearInterval(tickTimer); } catch { /* swallow */ }
  });

  return {
    notify: bump,
    activity,
    resetPage,
    getScore: () => decayedScore(Date.now()).score,
  };
}
